import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AiOutlinePlus } from "react-icons/ai";
import { GoUpload } from "react-icons/go";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import { useGetAllowance } from "../../../../services/allowance.service";
import { useGetEmployee } from "../../../../services/employee.service";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import BackPayModalForm from "../modals/BackPayModalForm";
import BackPayGroupModalForm from "../modals/BackPayGroupModalForm";
import { BACKPAY_CATEGORY, BackPayDto } from "../../../../models/back-pay.dto";
import {
	useDeleteBackPayGroup,
	useGetBackPayGroup,
	useGetBackPayGroupSearch,
} from "../../../../services/back-pay-group.service";
import {
	useDeleteBackPay,
	useGetBackPay,
	useGetBackPaySearch,
} from "../../../../services/back-pay.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { AllowanceOrderBy } from "../../../../models/allowance.dto";
import { PayPeriodOrderBy } from "../../../../models/pay-period.dto";
import APISearchInput from "../../../../components/search/APISearchInput";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import {
	PermissionChecker,
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import UploadBackPay from "../modals/UploadBackPay";
import { BsDownload } from "react-icons/bs";
import {
	backPayGroupColumns,
	backPayGroupColumnVisibility,
	backPayColumns,
	backPayColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import ExportBackPayModal from "../modals/ExportBackPayModal";

const BackPayView = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();

	// Tab state
	const [activeTab, setActiveTab] = useState<"backPayGroups" | "backPay">(
		"backPayGroups",
	);

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openBackPayGroupModal, setOpenBackPayGroupModal] = useState(false);
	const [openBackPayModal, setOpenBackPayModal] = useState(false);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [openUploadBackPayModal, setOpenUploadBackPayModal] = useState(false);

	const [searchParams, setSearchParams] = useSearchParams();

	// Column visibility for Back Pay Groups
	const {
		columnVisibilityModel: backPayGroupColumnVisibilityModel,
		setColumnVisibilityModel: setBackPayGroupColumnVisibilityModel,
	} = useColumnVisibilityModel(backPayGroupColumnVisibility);

	// Column visibility for Employee Back Pay
	const {
		columnVisibilityModel: backPayColumnVisibilityModel,
		setColumnVisibilityModel: setBackPayColumnVisibilityModel,
	} = useColumnVisibilityModel(backPayColumnVisibility);

	// Back Pay Groups table
	const { mutate: searchBackPayGroups } = useGetBackPayGroupSearch();
	const {
		tableAPIRef: backPayGroupTableAPIRef,
		fetchedTableData: fetchedBackPayGroupData,
		tableData: backPayGroupTableData,
		isLoading: backPayGroupLoading,
		pageCount: backPayGroupPageCount,
		setTableData: setBackPayGroupTableData,
	} = useManageTableWithSearchParams<BackPayDto>({
		fetchHook: (options) =>
			useGetBackPayGroup(currentCompany?.id as number, options),
		options: {},
	});

	// Employee Back Pay table
	const { mutate: searchBackPay } = useGetBackPaySearch();
	const {
		tableAPIRef: backPayTableAPIRef,
		fetchedTableData: fetchedBackPayData,
		tableData: backPayTableData,
		isLoading: backPayLoading,
		pageCount: backPayPageCount,
		setTableData: setBackPayTableData,
	} = useManageTableWithSearchParams<BackPayDto>({
		fetchHook: useGetBackPay,
		options: {
			companyId: currentCompany?.id,
		},
	});

	// Fetched data for modals
	const options = {
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
	};

	const { data: backPayGroups } = useGetBackPayGroup(
		currentCompany?.id as number,
		{
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: "createdAt:desc" as any,
		} as any,
	);
	const { data: employees } = useGetEmployee({
		...options,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});
	const { data: allowances } = useGetAllowance({
		...options,
		active: true,
		orderBy: AllowanceOrderBy.NAME_ASC,
	});
	const { data: payPeriods } = useGetPayPeriod({
		...options,
		orderBy: PayPeriodOrderBy.START_DATE_DESC,
	});

	const handleClose = () => setAnchorEl(null);

	const handleOpenBackPayGroupModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenBackPayGroupModal(true);
	};

	const handleOpenBackPayModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenBackPayModal(true);
	};

	const handleTabChange = (tab: "backPayGroups" | "backPay") => {
		setActiveTab(tab);
		const updated = new URLSearchParams(searchParams);
		updated.delete("page");
		updated.delete("pageSize");
		setSearchParams(updated);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteBackPayGroup } = useDeleteBackPayGroup();
	const { mutate: deleteBackPay } = useDeleteBackPay();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		if (activeTab === "backPayGroups") {
			deleteBackPayGroup(
				{ companyId: currentCompany?.id as number, id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		} else {
			deleteBackPay(
				{ id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		}
	};

	const handleOpenExportModal = () => {
		setOpenExportModal(true);
	};

	return (
		<div className="font-inter">
			<div className="flex justify-between items-center mt-4 mb-4">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={
							activeTab === "backPayGroups"
								? fetchedBackPayGroupData
								: fetchedBackPayData
						}
						setTableData={
							activeTab === "backPayGroups"
								? setBackPayGroupTableData
								: setBackPayTableData
						}
						mutate={
							activeTab === "backPayGroups"
								? searchBackPayGroups
								: searchBackPay
						}
					/>
					<TableFilter
						fields={
							activeTab === "backPayGroups"
								? [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Category",
											fieldName: "category",
											fieldType: "CHECKBOX_LIST",
											options: BACKPAY_CATEGORY,
										},
									]
								: [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Back Pay Group",
											fieldName: "groupId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
									]
						}
					/>
					<ManageTableColumns
						columns={
							activeTab === "backPayGroups"
								? (backPayGroupColumns as any)
								: (backPayColumns as any)
						}
						defaultVisibilityModel={
							activeTab === "backPayGroups"
								? backPayGroupColumnVisibility
								: backPayColumnVisibility
						}
						visibilityModel={
							activeTab === "backPayGroups"
								? backPayGroupColumnVisibilityModel
								: backPayColumnVisibilityModel
						}
						setVisibilityModel={
							activeTab === "backPayGroups"
								? setBackPayGroupColumnVisibilityModel
								: setBackPayColumnVisibilityModel
						}
						tableApiRef={
							activeTab === "backPayGroups"
								? backPayGroupTableAPIRef
								: backPayTableAPIRef
						}
					/>
				</div>
				<div className="flex items-center gap-4 font-medium">
					{/* Upload - Only show for Back Pay Groups tab */}
					{activeTab === "backPayGroups" && (
						<PermissionChecker
							permissionRule="OR"
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								onClick={() => setOpenUploadBackPayModal(true)}
								className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
							>
								<GoUpload className="mr-2 text-[17px] text-gray-500" />
								Upload Back Pay
							</button>
						</PermissionChecker>
					)}

					{/* Export - Only show for Employee Back Pay tab */}
					{activeTab === "backPay" && (
						<button
							onClick={handleOpenExportModal}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<BsDownload className="mr-2 text-[18px] text-gray-500" />
							Export Back Pay
						</button>
					)}

					<PermissionChecker
						permissionRule="OR"
						permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
					>
						{activeTab === "backPayGroups" ? (
							<button
								onClick={handleOpenBackPayGroupModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Back Pay Group
							</button>
						) : (
							<button
								onClick={handleOpenBackPayModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Employee Back Pay
							</button>
						)}
					</PermissionChecker>
				</div>
			</div>

			{/* Tabs */}
			<div className="flex border-b border-gray-200 mb-6">
				<button
					onClick={() => handleTabChange("backPayGroups")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "backPayGroups"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Back Pay Groups
				</button>
				<button
					onClick={() => handleTabChange("backPay")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "backPay"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Employee Back Pay
				</button>
			</div>

			{activeTab === "backPayGroups" ? (
				<TanStackEmployeeTable
					ref={backPayGroupTableAPIRef}
					tableId={TableId.BACK_PAY}
					data={backPayGroupTableData}
					columns={backPayGroupColumns}
					loading={backPayGroupLoading}
					columnVisibility={backPayGroupColumnVisibilityModel}
					onColumnVisibilityChange={setBackPayGroupColumnVisibilityModel}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.target as HTMLButtonElement);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget as HTMLButtonElement);
					}}
					initialColumnVisibility={backPayGroupColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={backPayGroupPageCount}
					apiRef={backPayGroupTableAPIRef}
				/>
			) : (
				<TanStackEmployeeTable
					ref={backPayTableAPIRef}
					tableId={TableId.BACK_PAY_GROUPS}
					data={backPayTableData}
					columns={backPayColumns}
					loading={backPayLoading}
					columnVisibility={backPayColumnVisibilityModel}
					onColumnVisibilityChange={setBackPayColumnVisibilityModel}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.target as HTMLButtonElement);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget as HTMLButtonElement);
					}}
					initialColumnVisibility={backPayColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={backPayPageCount}
					apiRef={backPayTableAPIRef}
				/>
			)}

			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={
					activeTab === "backPayGroups"
						? setOpenBackPayGroupModal
						: setOpenBackPayModal
				}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openBackPayGroupModal && (
				<BackPayGroupModalForm
					open={openBackPayGroupModal}
					setOpen={setOpenBackPayGroupModal}
					title={
						isEditForm ? "Update Back Pay Group" : "Add New Back Pay Group"
					}
					isEditForm={isEditForm}
					tableAPIRef={backPayGroupTableAPIRef}
					payPeriods={payPeriods?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openBackPayModal && (
				<BackPayModalForm
					open={openBackPayModal}
					setOpen={setOpenBackPayModal}
					title={isEditForm ? "Update Back Pay" : "Add New Back Pay"}
					isEditForm={isEditForm}
					tableAPIRef={backPayTableAPIRef}
					employees={employees?.data || []}
					allowances={allowances?.data || []}
					payPeriods={payPeriods?.data || []}
					backPayGroups={backPayGroups?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openUploadBackPayModal && (
				<UploadBackPay
					open={openUploadBackPayModal}
					setOpen={setOpenUploadBackPayModal}
				/>
			)}
			{openExportModal && currentCompany?.id && (
				<ExportBackPayModal
					open={openExportModal}
					setOpen={setOpenExportModal}
					companyId={currentCompany.id}
				/>
			)}
		</div>
	);
};

export default BackPayView;
